import type { PoolInventoryModel } from '.';
import { isDraftRowId, PENDING_ROW_PREFIX } from './draftRows';
import { createInventoryCsv } from './inventoryCsv';
import {
    clearRowFieldEdit,
    clearStagedChanges,
    clearSubmitError,
    closeSubmitReview,
    endCsvDownload,
    failSubmit,
    initialPoolInventoryState,
    openSubmitReview,
    poolInventoryReducer,
    resetEdits,
    setAddSymbolDraft,
    setAppliedSymbolSearch,
    setInventoryPage,
    setSymbolSearchQuery,
    stageRowEdit,
    stageRowRemoval,
    startCsvDownload,
    startSubmit,
    succeedSubmit,
    unstageRowRemoval,
} from './state';
import type { PoolInventoryAction, PoolInventoryState } from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    AddPoolItemsResponse,
    InventoryItem,
    ListPoolItemsResponse,
    NewPoolItem,
    UpdatePoolItemRequest,
} from '@thinkalpha/locates-ws-client';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import { log } from 'src/admin/LAB/log';
import { sanitizeFilenameSegment, triggerCsvDownload } from 'src/admin/LAB/PoolManagement/csvDownload';
import type {
    InventoryRow,
    InventoryRowEdit,
    InventoryRowsPerPage,
    StagedChangeEntry,
    StagedChangeSet,
    StagedFieldDelta,
    StagedRowBaseline,
} from 'src/admin/models/PoolInventoryModel';
import type { PoolInventoryUploadModel } from 'src/admin/models/PoolInventoryUploadModel';
import { PoolInventoryUploadModelImpl } from 'src/admin/models/PoolInventoryUploadModel/impl';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import {
    getAllPoolItemsQuery,
    getPoolItemsQuery,
    invalidatePoolChangeLogQuery,
    invalidatePoolItemsQuery,
} from 'src/queries/labPools';
import { addPoolItems, removePoolItem, updatePoolItem } from 'src/queries/labPoolsMutations';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Debounce window before a symbol-search keystroke is committed to the
 * backend `symbol` query parameter. Keeps live typing to a single
 * request once the user pauses rather than one exact-match request per
 * keystroke.
 */
const SYMBOL_SEARCH_DEBOUNCE_MS = 300;

/** Max concurrent per-symbol probes when reconciling a partially rejected add batch. */
const PERSISTENCE_PROBE_CONCURRENCY = 8;

/** Result of probing one pending add after a partially rejected batch ack. */
type PendingAddProbeOutcome = 'persisted' | 'missing' | 'unverified';

function fromApiItem(item: InventoryItem): InventoryRow {
    // Cleared prices come back as `null` (per backend "clear via PATCH
    // price: null" semantics). The current designs render the table cell
    // as a numeric input with no "no price" affordance, so coerce to 0
    // at the boundary. If/when we want a distinct "no price set" cell,
    // widen `InventoryRow.currentLabPrice` to `number | null` instead.
    return {
        id: item.id,
        symbol: item.symbol,
        currentQuantity: item.quantity,
        currentLabPrice: item.price ?? 0,
        borrowStatus: item.borrowStatus ?? null,
        firmBorrowStatus: item.firmBorrowStatus ?? null,
        quantityUsed: item.quantityUsed ?? null,
        dailyRevenue: item.symbolRevenueToday,
        available: item.available,
        currentPrice: item.currentPrice ?? null,
        fairValuePrice: item.fairValuePrice ?? null,
    };
}

/** Apply staged field edits on top of `rows` (shared by server and draft projections). */
function applyRowEdits(
    rows: ReadonlyArray<InventoryRow>,
    edits: Readonly<Record<string, InventoryRowEdit>>,
): ReadonlyArray<InventoryRow> {
    if (Object.keys(edits).length === 0) return rows;
    return rows.map((row) => {
        const edit = edits[row.id];
        if (!edit) return row;
        return {
            ...row,
            currentQuantity: edit.currentQuantity ?? row.currentQuantity,
            currentLabPrice: edit.currentLabPrice ?? row.currentLabPrice,
        };
    });
}

function toBaseline(row: InventoryRow | undefined): StagedRowBaseline | undefined {
    // Draft rows carry their own data and render as additions; baselines
    // only describe server rows.
    if (!row || isDraftRowId(row.id)) return undefined;
    return { symbol: row.symbol, currentQuantity: row.currentQuantity, currentLabPrice: row.currentLabPrice };
}

/**
 * Fold one staged field edit into a {@link StagedFieldDelta}: `null`
 * when the field is unedited or edited back to its baseline value (a
 * no-op that must neither preview nor submit). A missing baseline keeps
 * the edit with `before: null` — it still submits, just without a
 * before-value to show.
 */
function stagedFieldDelta(after: number | undefined, before: number | undefined): StagedFieldDelta | null {
    if (after === undefined) return null;
    if (before !== undefined && before === after) return null;
    return { before: before ?? null, after };
}

/** One backend call the submit fans out, tagged for partial-failure bookkeeping. */
type SubmitOperation =
    | { kind: 'update'; rowId: string; promise: Promise<unknown> }
    | { kind: 'remove'; rowId: string; promise: Promise<unknown> }
    | { kind: 'add-batch'; rowIds: string[]; promise: Promise<AddPoolItemsResponse> };

/** The created-pool upload wizard bypasses the pool's write gate (see `openUploadWizardForCreatedPool`). */
const ALWAYS_WRITABLE: ReadonlySignal<boolean> = signal(true);

/** Everything the inventory model needs from its owner (the pool side). */
export interface PoolInventoryModelDeps {
    /** The pool whose inventory this is; part of the instance's identity. */
    readonly poolId: string;
    readonly queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    readonly queryClient: ReactBindings['QueryClient'];
    readonly store: ReactBindings['Store'];
    /** Write access on the pool; every staging/submit command fails closed without it. */
    readonly canWrite: ReadonlySignal<boolean>;
    /** Legacy pools silently ignore availability, so the toggle fails closed. */
    readonly isLegacyPool: ReadonlySignal<boolean>;
    /** Firm Borrow Status column gate per the `enable-lab-firm-borrow-status` flag. */
    readonly showFirmBorrowStatus: ReadonlySignal<boolean>;
    /** Pool display name for the CSV filename; falls back to `pool`. */
    readonly poolName: ReadonlySignal<string | null>;
    /**
     * Page-size preference. Owned by the parent (and shared across pool
     * switches — page size deliberately sticks while the page itself
     * resets with each instance); this model reads and writes it.
     */
    readonly rowsPerPage: Signal<InventoryRowsPerPage>;
    /** A mutation refetched the server rows — the owner bumps its freshness stamp. */
    onServerRowsRefetched(): void;
}

/** See {@link PoolInventoryModel}. */
export class PoolInventoryModelImpl implements PoolInventoryModel {
    readonly #disposableStack = new DisposableStack();
    readonly #deps: PoolInventoryModelDeps;
    readonly #state = signal<PoolInventoryState>(initialPoolInventoryState);
    readonly #itemsQuery: QueryObserverModel<ListPoolItemsResponse, Error, ListPoolItemsResponse>;

    readonly poolId: string;
    readonly canWrite: ReadonlySignal<boolean>;
    readonly isLegacy: ReadonlySignal<boolean>;
    readonly showFirmBorrowStatus: ReadonlySignal<boolean>;

    /**
     * Locally staged additions, newest first. Per-instance — unlike the
     * old cross-pool map, a confirmed discard or navigation disposes
     * them with the model, so "discard changes" really discards.
     */
    readonly #pendingAdds = signal<ReadonlyArray<InventoryRow>>([]);
    /** Optimistic per-row availability overlays while a PATCH settles. */
    readonly #itemAvailabilityOverrides = signal<ReadonlyMap<string, boolean>>(new Map());
    /** Per-row monotonic counters so a stale availability settlement is dropped. */
    readonly #itemAvailabilityMutationVersion = new Map<string, number>();

    constructor(deps: PoolInventoryModelDeps) {
        this.#deps = deps;
        this.poolId = deps.poolId;
        this.canWrite = deps.canWrite;
        this.isLegacy = deps.isLegacyPool;
        this.showFirmBorrowStatus = deps.showFirmBorrowStatus;

        const itemsOptions = computed<QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse>>(() => {
            const s = this.#state.value;
            // A symbol search is answered by the backend: pass the
            // committed term as the `symbol` parameter. The endpoint
            // returns every matching item and ignores skip/limit, so we
            // don't page it here.
            const search = s.appliedSymbolSearch.trim();
            if (search !== '') {
                return getPoolItemsQuery(deps.poolId, { symbol: search });
            }
            // "All" rows-per-page loads the full pool (the Daily Total
            // summary needs every row), paging the backend for real
            // rather than requesting one oversized limit. Otherwise page
            // server-side with the current page / page-size.
            if (deps.rowsPerPage.value === 'all') {
                return getAllPoolItemsQuery(deps.poolId);
            }
            const perPage = deps.rowsPerPage.value;
            // Guard against NaN / Infinity / fractional page state — the
            // reducer doesn't constrain `SET_INVENTORY_PAGE` callers, so
            // a bad caller could otherwise propagate a garbage `skip`
            // into the request. Display-side clamping against
            // `pageCount` would close the loop on stale rows when the
            // total shrinks, but reading the count here creates a
            // signal cycle (the count comes from this very query).
            const requested = s.inventoryPage;
            const page = Number.isFinite(requested) ? Math.max(1, Math.trunc(requested)) : 1;
            return getPoolItemsQuery(deps.poolId, { skip: (page - 1) * perPage, limit: perPage });
        });
        this.#itemsQuery = deps.queryObserverModelFactory<ListPoolItemsResponse, Error, ListPoolItemsResponse>(
            itemsOptions,
        );
        this.#disposableStack.use(this.#itemsQuery);

        // Drop any pending debounced symbol search so it can't dispatch
        // into a disposed model.
        this.#disposableStack.defer(() => this.#applySymbolSearch.cancel());
    }

    #dispatch(action: PoolInventoryAction): void {
        this.#state.value = poolInventoryReducer(this.#state.value, action);
    }

    // --- Rows ---
    readonly isLoading: ReadonlySignal<boolean> = computed(() => this.#itemsQuery.signal.value.isLoading);
    readonly error: ReadonlySignal<string | null> = computed(
        () => this.#itemsQuery.signal.value.error?.message ?? null,
    );

    readonly serverRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        // Availability PATCHes render optimistically: overlay the requested
        // value until the mutation settles and the refetch reseats it.
        const overrides = this.#itemAvailabilityOverrides.value;
        const remote =
            this.#itemsQuery.signal.value.data?.items.map(fromApiItem).map((row) => {
                const override = overrides.get(row.id);
                return override === undefined || override === row.available ? row : { ...row, available: override };
            }) ?? [];
        const pending = this.#pendingAdds.value;
        if (pending.length === 0) return remote;
        // Pending adds sit ABOVE server rows so a freshly-added symbol
        // is visible at the top of the list without scrolling. Within
        // the pending block, `#addSymbolInternal` keeps newest-first.
        return [...pending, ...remote];
    });

    readonly rowEdits: ReadonlySignal<Readonly<Record<string, InventoryRowEdit>>> = computed(
        () => this.#state.value.rowEdits,
    );

    readonly stagedRowBaselines: ReadonlySignal<Readonly<Record<string, StagedRowBaseline>>> = computed(
        () => this.#state.value.stagedRowBaselines,
    );

    /**
     * Memoised on the reducer's `pendingRowRemovals` array reference so
     * unrelated state changes (e.g. search query updates) don't churn a
     * fresh `Set` and propagate a "changed" event to every subscriber.
     */
    readonly pendingRowRemovals: ReadonlySignal<ReadonlySet<string>> = (() => {
        let lastSource: ReadonlyArray<string> | null = null;
        let lastSet: ReadonlySet<string> = new Set();
        return computed(() => {
            const source = this.#state.value.pendingRowRemovals;
            if (source !== lastSource) {
                lastSource = source;
                lastSet = new Set(source);
            }
            return lastSet;
        });
    })();

    readonly displayedRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() =>
        applyRowEdits(this.serverRows.value, this.#state.value.rowEdits),
    );

    readonly pendingDraftRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const pending = this.#pendingAdds.value;
        if (pending.length === 0) return [];
        return applyRowEdits(pending, this.#state.value.rowEdits);
    });

    readonly filteredRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        // Server rows for the current view, excluding drafts (which
        // render in their own always-visible section above the table).
        // When a symbol search is active the backend has already
        // narrowed these to the matching items, so there is no
        // client-side filtering left to do here.
        return this.displayedRows.value.filter((r) => !isDraftRowId(r.id));
    });

    readonly stagedChanges: ReadonlySignal<StagedChangeSet> = computed(() => {
        const baselines = this.stagedRowBaselines.value;
        const removals = this.pendingRowRemovals.value;
        const edits = this.rowEdits.value;
        const entries: StagedChangeEntry[] = [];
        for (const draft of this.pendingDraftRows.value) {
            entries.push({
                kind: 'added',
                rowId: draft.id,
                symbol: draft.symbol,
                quantity: draft.currentQuantity,
                price: draft.currentLabPrice,
            });
        }
        for (const rowId of removals) {
            // Draft rows never stage removals (`removeRow` drops them from
            // the pending adds directly); skipped defensively.
            if (isDraftRowId(rowId)) continue;
            const baseline = baselines[rowId];
            entries.push({
                kind: 'removed',
                rowId,
                symbol: baseline?.symbol ?? null,
                quantity: baseline?.currentQuantity ?? null,
                price: baseline?.currentLabPrice ?? null,
            });
        }
        for (const [rowId, edit] of Object.entries(edits)) {
            // Draft-row edits fold into the addition above.
            if (isDraftRowId(rowId)) continue;
            // A removal supersedes the row's edits — it's listed above.
            if (removals.has(rowId)) continue;
            const baseline = baselines[rowId];
            // Compare raw values, not display formatting: submit PATCHes
            // the raw staged value, so anything it would send must be an
            // entry even when the change is below display precision.
            const quantity = stagedFieldDelta(edit.currentQuantity, baseline?.currentQuantity);
            const price = stagedFieldDelta(edit.currentLabPrice, baseline?.currentLabPrice);
            // Every field edited back to its baseline → nothing to submit.
            if (quantity === null && price === null) continue;
            entries.push({ kind: 'updated', rowId, symbol: baseline?.symbol ?? null, quantity, price });
        }
        // Group order: updated, added, removed — matches the review
        // dialog's visual rhythm (changes first, additions, deletions
        // last); baseline-less symbols sort first within their group.
        const rank: Record<StagedChangeEntry['kind'], number> = { updated: 0, added: 1, removed: 2 };
        entries.sort((a, b) => {
            const d = rank[a.kind] - rank[b.kind];
            if (d !== 0) return d;
            return (a.symbol ?? '').localeCompare(b.symbol ?? '');
        });
        const counts = { added: 0, updated: 0, removed: 0 };
        for (const entry of entries) counts[entry.kind]++;
        return { entries, counts };
    });

    readonly totalDailyRevenue: ReadonlySignal<number | null> = computed(() => {
        // `dailyRevenue` is symbol-level: a symbol offered at several
        // prices repeats the same figure on each of its rows, so count
        // each symbol once.
        const seen = new Set<string>();
        let sum = 0;
        for (const r of this.serverRows.value) {
            if (r.dailyRevenue === null || seen.has(r.symbol)) continue;
            seen.add(r.symbol);
            sum += r.dailyRevenue;
        }
        // Null when no server row is known yet so the toolbar renders an
        // em-dash placeholder instead of "$0.00" while loading.
        return seen.size > 0 ? sum : null;
    });

    // --- Pagination ---
    readonly rowsPerPage: ReadonlySignal<InventoryRowsPerPage> = computed(() => this.#deps.rowsPerPage.value);
    /**
     * Total number of server-side items in the pool. Backed by the
     * `count` field added in locates-ws #449. Pending draft adds are
     * deliberately excluded — they render in their own always-visible
     * section above the paginated table and don't participate in the
     * "showing X-Y of N" label. Returns `0` while the query hasn't
     * reported a count yet.
     */
    readonly totalCount: ReadonlySignal<number> = computed(() => this.#itemsQuery.signal.value.data?.count ?? 0);
    readonly pageCount: ReadonlySignal<number> = computed(() => {
        const perPage = this.#deps.rowsPerPage.value;
        if (perPage === 'all') return 1;
        // `totalCount` is the right total in both modes: a paged fetch
        // reports the full pool size, and a symbol search reports the
        // match count (the backend sets `count === items.length`).
        return Math.max(1, Math.ceil(this.totalCount.value / perPage));
    });
    /**
     * Clamped page index. The stored page is user-driven and could be
     * left out-of-range after the filter narrows; this view snaps it
     * back into `[1, pageCount]` so consumers don't have to.
     */
    readonly page: ReadonlySignal<number> = computed(() => {
        const requested = this.#state.value.inventoryPage;
        const max = this.pageCount.value;
        if (!Number.isFinite(requested) || requested < 1) return 1;
        if (requested > max) return max;
        return Math.floor(requested);
    });
    readonly paginatedRows: ReadonlySignal<ReadonlyArray<InventoryRow>> = computed(() => {
        const s = this.#state.value;
        const filtering = s.appliedSymbolSearch.trim() !== '';
        const perPage = this.#deps.rowsPerPage.value;
        // Filter-active and "all" both bypass server paging (the symbol
        // search returns every match), so the slice is purely client-side.
        // Drafts are excluded here — they render in their own section
        // above the table and never participate in filter / page math.
        if (filtering || perPage === 'all') {
            const rows = this.filteredRows.value;
            if (perPage === 'all' || !filtering) return rows;
            // Filtering with a numeric page size: still slice client-side
            // so the user can page through search results.
            const page = this.page.value;
            const start = (page - 1) * perPage;
            return rows.slice(start, start + perPage);
        }
        // Paged server fetch: the query already holds the right page;
        // we just need to strip any draft rows that snuck in via
        // `displayedRows` (drafts render in their own section above the
        // table — see {@link pendingDraftRows}).
        return this.displayedRows.value.filter((r) => !isDraftRowId(r.id));
    });
    setPage(page: number): void {
        this.#dispatch(setInventoryPage(page));
    }
    setRowsPerPage(rowsPerPage: InventoryRowsPerPage): void {
        if (this.#deps.rowsPerPage.value === rowsPerPage) return;
        this.#deps.rowsPerPage.value = rowsPerPage;
        // Changing rows-per-page invalidates the current page index;
        // snap to 1 so the offset math is well-defined.
        this.#dispatch(setInventoryPage(1));
    }

    // --- Symbol search ---
    readonly symbolSearchQuery: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchQuery);
    setSymbolSearchQuery(query: string): void {
        // Update the input text immediately so typing stays responsive,
        // then commit the backend `symbol` filter on a debounce.
        this.#dispatch(setSymbolSearchQuery(query));
        if (query.trim() === '') {
            // Clearing the box should restore the full (paged) pool right
            // away — there's nothing to debounce.
            this.#applySymbolSearch.cancel();
            this.#dispatch(setAppliedSymbolSearch(''));
            return;
        }
        this.#applySymbolSearch();
    }

    /**
     * Commits the current search box text to the backend `symbol`
     * filter. Debounced so live typing fires a single exact-match
     * request once the user pauses rather than one per keystroke.
     */
    readonly #applySymbolSearch = debounce(() => {
        // Symbols are stored upper-case and the backend filter is a
        // case-sensitive `sym = @sym` match, so normalize the typed term
        // the same way the add-symbol path does before sending it.
        this.#dispatch(setAppliedSymbolSearch(this.#state.value.symbolSearchQuery.trim().toUpperCase()));
    }, SYMBOL_SEARCH_DEBOUNCE_MS);

    // --- Staged work ---
    readonly addSymbolDraft: ReadonlySignal<string> = computed(() => this.#state.value.addSymbolDraft);
    readonly isDirty: ReadonlySignal<boolean> = computed(() => {
        const s = this.#state.value;
        if (Object.keys(s.rowEdits).length > 0) return true;
        if (s.pendingRowRemovals.length > 0) return true;
        // A typed-but-not-yet-added symbol counts as unsaved work: any
        // navigation/reset guard that consults `isDirty` should prompt the
        // user before discarding it.
        if (s.addSymbolDraft.trim() !== '') return true;
        if (this.#pendingAdds.value.length > 0) return true;
        return false;
    });
    readonly unsavedChangeCount: ReadonlySignal<number> = computed(() => {
        const counts = this.stagedChanges.value.counts;
        return counts.added + counts.updated + counts.removed;
    });

    setAddSymbolDraft(value: string): void {
        if (!this.#deps.canWrite.value) return;
        this.#dispatch(setAddSymbolDraft(value));
    }
    async addSymbolFromDraft(): Promise<void> {
        if (!this.#deps.canWrite.value) return;
        // Block while a submit is settling: a draft staged mid-flight would
        // be wiped when the submit's success path clears the pending adds,
        // silently discarding it without a POST.
        if (this.#state.value.isSubmitting) return;
        const draft = this.#state.value.addSymbolDraft.trim();
        if (!draft) return;
        // Only clear the draft when the symbol actually lands. A rejected
        // add (duplicate, …) preserves the user's input so they can
        // correct it instead of silently losing what they typed.
        if (await this.#addSymbolInternal(draft)) {
            this.#dispatch(setAddSymbolDraft(''));
        }
    }
    async addSymbol(symbol: string): Promise<void> {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        await this.#addSymbolInternal(symbol);
    }

    /** `true` when `symbol` is on the loaded items page or staged as a pending add. */
    #hasSymbolLocally(symbol: string): boolean {
        const serverItems = this.#itemsQuery.signal.value.data?.items ?? [];
        if (serverItems.some((r) => r.symbol === symbol)) return true;
        return this.#pendingAdds.value.some((r) => r.symbol === symbol);
    }

    #toastAddSymbolDuplicate(symbol: string): void {
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: t('admin::pool-management::toast-add-symbol-duplicate', { symbol }),
                group: 'default',
                variant: 'error',
            },
        });
    }

    async #addSymbolInternal(rawSymbol: string): Promise<boolean> {
        const symbol = rawSymbol.trim().toUpperCase();
        if (!symbol) return false;
        const poolId = this.poolId;
        if (this.#hasSymbolLocally(symbol)) {
            this.#toastAddSymbolDuplicate(symbol);
            return false;
        }
        // The items page only holds the current table page, so a page-local
        // check misses symbols elsewhere in the pool and the add would
        // duplicate them (PLAT-12841). Ask the backend's exact-match filter,
        // which spans the whole pool regardless of pagination.
        try {
            const options = getPoolItemsQuery(poolId, { symbol });
            // `staleTime: 0` forces a network refetch: a correctness probe
            // must not be satisfied by the query's 30s-fresh cache entry,
            // which could miss a concurrent insert by another client.
            const response = await this.#deps.queryClient.fetchQuery({
                ...options,
                queryKey: options.queryKey ?? [],
                staleTime: 0,
            });
            if (this.#abort.signal.aborted) return false;
            if (response.items.length > 0) {
                this.#toastAddSymbolDuplicate(symbol);
                return false;
            }
        } catch (error) {
            if (this.#abort.signal.aborted) return false;
            const message = error instanceof Error ? error.message : String(error);
            this.#deps.store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-add-symbol-error', { symbol, message }),
                    group: 'default',
                    variant: 'error',
                },
            });
            return false;
        }
        // Re-validate after the await: a concurrent add may have staged the
        // same symbol, or a submit may have started meanwhile. Staging into
        // an in-flight submit would let its success path wipe the row via
        // the blanket pending-adds clear without the row ever being POSTed.
        if (this.#state.value.isSubmitting) return false;
        if (this.#hasSymbolLocally(symbol)) return false;
        const newRow: InventoryRow = {
            id: `${PENDING_ROW_PREFIX}${poolId}_${symbol.toLowerCase()}_${Date.now()}`,
            symbol,
            currentQuantity: 0,
            currentLabPrice: 0,
            borrowStatus: null,
            firmBorrowStatus: null,
            quantityUsed: null,
            dailyRevenue: null,
            available: true,
            currentPrice: null,
            fairValuePrice: null,
        };
        // Prepend so the newest add is at the top of the pending block,
        // which (combined with the `[...pending, ...remote]` merge) puts
        // it at row 0 of the table.
        this.#pendingAdds.value = [newRow, ...this.#pendingAdds.value];
        return true;
    }

    editRowQuantity(rowId: string, value: number): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // Quantities must be non-negative integers: the inventory CSV
        // round-trip only parses integer share counts (see
        // `parseInventoryCsv`), and the table renders them with
        // thousands separators that imply whole shares. Reject
        // fractional / Infinity / NaN inputs so a stray decimal isn't
        // silently truncated on export.
        if (!Number.isInteger(value) || value < 0) return;
        // A value matching the underlying row is a no-op (e.g. focusing
        // then blurring the field without typing). Clear any staged edit
        // instead of staging it, so the row doesn't show as dirty.
        const original = this.serverRows.value.find((r) => r.id === rowId);
        if (original && original.currentQuantity === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentQuantity: value }, toBaseline(original)));
    }
    editRowLabPrice(rowId: string, value: number): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (!Number.isFinite(value) || value < 0) return;
        // See `editRowQuantity`: re-applying the row's existing price (the
        // `0`-price focus/blur case) must not dirty the row.
        const original = this.serverRows.value.find((r) => r.id === rowId);
        if (original && original.currentLabPrice === value) {
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        this.#dispatch(stageRowEdit({ rowId, currentLabPrice: value }, toBaseline(original)));
    }
    clearRowQuantityEdit(rowId: string): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
    }
    clearRowLabPriceEdit(rowId: string): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
    }
    removeRow(rowId: string): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        if (isDraftRowId(rowId)) {
            // It's a locally-added row that never went to the backend; just drop it.
            this.#pendingAdds.value = this.#pendingAdds.value.filter((r) => r.id !== rowId);
            // Drop any staged field edits keyed on this draft id so they
            // don't orphan in `rowEdits` and leave `isDirty` diverging
            // from `unsavedChangeCount`.
            this.#dispatch(clearRowFieldEdit(rowId, 'currentQuantity'));
            this.#dispatch(clearRowFieldEdit(rowId, 'currentLabPrice'));
            return;
        }
        // Capture the row's server values so the submit-review preview can
        // list the removal without a full-pool fetch (PLAT-12889). The row
        // is on the visible page (the action came from its row), so the
        // lookup only misses if the id went stale — the preview then skips
        // it, matching the old snapshot behavior for stale ids.
        const original = this.serverRows.value.find((r) => r.id === rowId);
        this.#dispatch(stageRowRemoval(rowId, toBaseline(original)));
    }
    restoreRow(rowId: string): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        this.#dispatch(unstageRowRemoval(rowId));
    }
    resetEdits(): void {
        if (!this.#deps.canWrite.value) return;
        if (this.#state.value.isSubmitting) return;
        // The RESET_EDITS reducer case also clears `submitError` so the
        // user isn't stuck on a dead-end error after resetting.
        this.#dispatch(resetEdits());
        this.#pendingAdds.value = [];
    }

    // --- Submit lifecycle ---
    readonly submitReviewOpen: ReadonlySignal<boolean> = computed(() => this.#state.value.submitReviewOpen);
    readonly isSubmitting: ReadonlySignal<boolean> = computed(() => this.#state.value.isSubmitting);
    readonly submitError: ReadonlySignal<string | null> = computed(() => this.#state.value.submitError);

    openSubmitReview(): void {
        if (!this.#deps.canWrite.value) return;
        this.#dispatch(openSubmitReview());
    }
    closeSubmitReview(): void {
        this.#dispatch(closeSubmitReview());
    }
    clearSubmitError(): void {
        this.#dispatch(clearSubmitError());
    }

    /**
     * Translate staged edits/removals/pending-adds into the per-item
     * backend operations. The locates-ws API doesn't expose a bulk
     * preview/apply, so the model fans out the change set into one
     * `POST .../items` (batched), N `PATCH .../items/{id}`, and N
     * `DELETE .../items/{id}` calls and waits for all to settle.
     *
     * Each op is tagged so a partial failure can leave only the
     * failed pieces staged for retry — succeeded edits/removals are
     * cleared, and pending-adds are dropped only when their batch POST
     * actually committed.
     */
    async submitChanges(): Promise<boolean> {
        if (this.#abort.signal.aborted) return false;
        if (!this.#deps.canWrite.value) return false;
        if (this.#state.value.isSubmitting) return false;
        const poolId = this.poolId;

        const operations = this.#buildSubmitOperations(poolId);
        if (operations.length === 0) {
            this.#dispatch(resetEdits());
            this.#pendingAdds.value = [];
            this.#dispatch(closeSubmitReview());
            return true;
        }
        // Only enter the submitting lifecycle once we know there's real
        // work — otherwise the no-op path leaves `isSubmitting` stuck
        // true because nothing dispatches `succeedSubmit`.
        this.#dispatch(startSubmit());
        const totalOps = operations.length;
        const toastId = this.#startActionToast(
            t('admin::pool-management::toast-submit-in-progress', { count: totalOps }),
        );

        try {
            const results = await Promise.allSettled(operations.map((o) => o.promise));
            // A pool switch disposed this instance mid-submit: the writes
            // are the backend's now; nothing may land on the dead instance.
            if (this.#abort.signal.aborted) {
                // Nothing may land on the dead instance, but the persistent
                // in-progress toast belongs to the page, not the model.
                this.#deps.store.dispatch({ type: 'toast::dismiss', id: toastId });
                return false;
            }
            // Cache invalidation is best-effort: the writes have settled, so
            // a rejecting refetch must not report the submit as failed —
            // that would keep persisted adds staged and re-POST them on
            // retry. The next refetch reconciles.
            let refreshed = false;
            try {
                await Promise.all([
                    invalidatePoolItemsQuery(this.#deps.queryClient, poolId),
                    invalidatePoolChangeLogQuery(this.#deps.queryClient, poolId),
                ]);
                refreshed = true;
            } catch (error) {
                // Fall through — the staged-change bookkeeping below is
                // what matters for correctness.
                log.warn(
                    `Post-submit cache refresh for pool ${poolId} rejected; the submitted writes stand and staged-change bookkeeping continues`,
                    { error, poolId },
                );
            }
            if (this.#abort.signal.aborted) {
                // Nothing may land on the dead instance, but the persistent
                // in-progress toast belongs to the page, not the model.
                this.#deps.store.dispatch({ type: 'toast::dismiss', id: toastId });
                return false;
            }
            // The owner's freshness stamp only advances when the refetch
            // actually settled; a rejected refresh must not read as fresh.
            if (refreshed) this.#deps.onServerRowsRefetched();

            // Walk results once, partitioning ops by outcome so partial
            // failures keep only the failed staged changes around.
            const successfulEditIds: string[] = [];
            const successfulRemovalIds: string[] = [];
            let addBatch: { rowIds: string[]; ack: AddPoolItemsResponse } | null = null;
            let firstFailure: unknown = null;
            for (let i = 0; i < operations.length; i++) {
                const op = operations[i]!;
                const res = results[i]!;
                if (res.status === 'fulfilled') {
                    if (op.kind === 'update') successfulEditIds.push(op.rowId);
                    else if (op.kind === 'remove') successfulRemovalIds.push(op.rowId);
                    // Already settled (checked above), so this resolves at once.
                    else addBatch = { rowIds: op.rowIds, ack: await op.promise };
                } else if (firstFailure === null) {
                    firstFailure = res.reason;
                }
            }

            // A fulfilled POST can still reject rows up front (invalid price
            // or quantity) — the ack carries only counts, so when anything
            // was rejected, probe which pending symbols actually landed and
            // keep the rest staged for correction instead of silently
            // dropping them behind a success toast. Known limitation: a
            // symbol match is presence, not ownership — if another client
            // inserted the same symbol between the preflight and this POST,
            // our rejected row reads as persisted. Closing that needs
            // per-row acceptance in the backend ack.
            let rejectedAdds = 0;
            let unverifiedAdds = 0;
            if (addBatch !== null) {
                let persistedIds: ReadonlyArray<string> = addBatch.rowIds;
                if (addBatch.ack.rejected > 0) {
                    const probe = await this.#persistedPendingAddIds(addBatch.rowIds);
                    persistedIds = probe.persistedIds;
                    unverifiedAdds = probe.unverifiedCount;
                    if (this.#abort.signal.aborted) {
                        // Nothing may land on the dead instance, but the persistent
                        // in-progress toast belongs to the page, not the model.
                        this.#deps.store.dispatch({ type: 'toast::dismiss', id: toastId });
                        return false;
                    }
                    // Rows the probe could not check stay staged too, but are
                    // reported as unverified rather than rejected. The ack's
                    // count caps what may be called rejected: any further
                    // missing rows (e.g. an accepted row whose async insert
                    // hadn't landed at probe time) are unverified, not
                    // rejected.
                    const missingAdds = addBatch.rowIds.length - persistedIds.length - unverifiedAdds;
                    rejectedAdds = Math.min(missingAdds, addBatch.ack.rejected);
                    unverifiedAdds += missingAdds - rejectedAdds;
                    // The backend reported rejections the probe could not
                    // attribute (every symbol present — e.g. inserted by
                    // another client meanwhile). Presence is not proof our
                    // rows landed: keep the whole batch staged and report it
                    // as unverified for the user to reconcile.
                    if (rejectedAdds === 0 && unverifiedAdds === 0) {
                        persistedIds = [];
                        unverifiedAdds = addBatch.ack.rejected;
                    }
                }
                const persisted = new Set(persistedIds);
                this.#pendingAdds.value = this.#pendingAdds.value.filter((row) => !persisted.has(row.id));
                // Clear any staged edits keyed by the persisted pending-row
                // ids so they don't orphan in `rowEdits` once the rows are
                // gone from the pending adds.
                successfulEditIds.push(...persistedIds);
            }

            if (successfulEditIds.length > 0 || successfulRemovalIds.length > 0) {
                // A successful DELETE also invalidates any staged edit on
                // that row (edits are skipped for removed rows at op-build
                // time, so they never got their own op). Clear both, or a
                // partial failure would resurrect the edit as a phantom
                // "updated" entry in the review dialog and re-PATCH a row
                // the backend already deleted (404) on retry.
                this.#dispatch(
                    clearStagedChanges([...successfulEditIds, ...successfulRemovalIds], successfulRemovalIds),
                );
            }
            if (firstFailure !== null || rejectedAdds > 0 || unverifiedAdds > 0) {
                // Report rejected and unverifiable rows separately — both
                // can occur in one submit and the user needs both counts.
                const addProblems: string[] = [];
                if (rejectedAdds > 0) {
                    addProblems.push(t('admin::pool-management::submit-error-rejected-adds', { count: rejectedAdds }));
                }
                if (unverifiedAdds > 0) {
                    addProblems.push(
                        t('admin::pool-management::submit-error-unverified-adds', { count: unverifiedAdds }),
                    );
                }
                // A failed PATCH/DELETE and add-batch problems can coincide;
                // the message carries every part so nothing is hidden.
                if (firstFailure !== null) {
                    addProblems.unshift(firstFailure instanceof Error ? firstFailure.message : String(firstFailure));
                }
                const message = addProblems.join(' ');
                this.#dispatch(failSubmit(message));
                this.#replaceToast(toastId, {
                    variant: 'error',
                    message: t('admin::pool-management::toast-submit-error', { message }),
                });
                return false;
            }

            this.#dispatch(succeedSubmit());
            this.#dispatch(resetEdits());
            this.#pendingAdds.value = [];
            this.#dispatch(closeSubmitReview());
            this.#replaceToast(toastId, {
                variant: 'success',
                message: t('admin::pool-management::toast-submit-success', { count: totalOps }),
            });
            return true;
        } catch (e) {
            const message = e instanceof Error ? e.message : t('admin::pool-management::submit-error-generic');
            this.#dispatch(failSubmit(message));
            this.#replaceToast(toastId, {
                variant: 'error',
                message: t('admin::pool-management::toast-submit-error', { message }),
            });
            return false;
        }
    }

    /**
     * Which of the given pending-add rows the backend actually persisted,
     * probed per symbol with the same whole-pool exact-match filter the
     * add path uses (the items page only holds the current page). A probe
     * that fails keeps its row staged — conservative for data loss: the
     * user can drop a duplicate, but a silently vanished row is gone —
     * and is counted separately so the failure isn't blamed on the row.
     */
    async #persistedPendingAddIds(
        rowIds: ReadonlyArray<string>,
    ): Promise<{ persistedIds: ReadonlyArray<string>; unverifiedCount: number }> {
        const pending = new Map(this.#pendingAdds.value.map((row): [string, InventoryRow] => [row.id, row]));
        const checks: Array<{ rowId: string; outcome: PendingAddProbeOutcome }> = [];
        // Bounded fan-out: a large staged batch must not open one request
        // per row all at once.
        for (let start = 0; start < rowIds.length; start += PERSISTENCE_PROBE_CONCURRENCY) {
            // Disposal mid-probe: the caller discards the result, so don't
            // open further requests on a dead instance.
            if (this.#abort.signal.aborted) break;
            const chunk = rowIds.slice(start, start + PERSISTENCE_PROBE_CONCURRENCY);
            checks.push(...(await Promise.all(chunk.map((rowId) => this.#probePendingAdd(pending, rowId)))));
        }
        return {
            persistedIds: checks.filter((check) => check.outcome === 'persisted').map((check) => check.rowId),
            unverifiedCount: checks.filter((check) => check.outcome === 'unverified').length,
        };
    }

    async #probePendingAdd(
        pending: ReadonlyMap<string, InventoryRow>,
        rowId: string,
    ): Promise<{ rowId: string; outcome: PendingAddProbeOutcome }> {
        return (async () => {
            const row = pending.get(rowId);
            // An id no longer in the drafts has nothing left to keep staged.
            if (!row) return { rowId, outcome: 'persisted' as const };
            if (this.#abort.signal.aborted) return { rowId, outcome: 'unverified' as const };
            try {
                const options = getPoolItemsQuery(this.poolId, { symbol: row.symbol });
                const response = await this.#deps.queryClient.fetchQuery({
                    ...options,
                    queryKey: options.queryKey ?? [],
                    staleTime: 0,
                });
                // `symbol` is an exact-match filter, so any hit is this row.
                return { rowId, outcome: response.items.length > 0 ? ('persisted' as const) : ('missing' as const) };
            } catch (error) {
                log.warn(`Probing pool ${this.poolId} for ${row.symbol} after a partial add failed`, {
                    error,
                    poolId: this.poolId,
                    symbol: row.symbol,
                });
                return { rowId, outcome: 'unverified' as const };
            }
        })();
    }

    #buildSubmitOperations(poolId: string): SubmitOperation[] {
        // Operations derive from the same canonical change set the review
        // dialog renders, so preview and execution agree by construction.
        //
        // Staged ids are trusted as-is: they were captured from real server
        // rows when staged. Requiring them to still be on the CURRENT items
        // page would silently drop changes staged from another page or from
        // a since-cleared symbol search (PLAT-12842) — the items query only
        // holds one page since server-side pagination landed. If an id has
        // gone stale (row deleted or replaced elsewhere), the call 404s and
        // the partial-failure path keeps that change staged with an error.
        const { entries } = this.stagedChanges.value;

        const ops: SubmitOperation[] = [];
        const addRowIds: string[] = [];
        const newItems: NewPoolItem[] = [];
        for (const entry of entries) {
            if (entry.kind === 'updated') {
                const patch: UpdatePoolItemRequest = {};
                if (entry.quantity) patch.quantity = entry.quantity.after;
                if (entry.price) patch.price = entry.price.after;
                ops.push({
                    kind: 'update',
                    rowId: entry.rowId,
                    promise: updatePoolItem({ poolId, itemId: entry.rowId, request: patch }),
                });
            } else if (entry.kind === 'removed') {
                ops.push({
                    kind: 'remove',
                    rowId: entry.rowId,
                    promise: removePoolItem({ poolId, itemId: entry.rowId }),
                });
            } else {
                addRowIds.push(entry.rowId);
                newItems.push({ symbol: entry.symbol, quantity: entry.quantity, price: entry.price });
            }
        }
        // POST all pending adds as one batch, matching the backend's
        // bulk-add contract.
        if (newItems.length > 0) {
            ops.push({
                kind: 'add-batch',
                rowIds: addRowIds,
                promise: addPoolItems({ poolId, request: { items: newItems } }),
            });
        }

        return ops;
    }

    // --- Item availability (suspend / publish) ---
    readonly pendingAvailabilityRowIds: ReadonlySignal<ReadonlySet<string>> = computed(
        () => new Set(this.#itemAvailabilityOverrides.value.keys()),
    );

    setItemAvailability(rowId: string, available: boolean): void {
        const poolId = this.poolId;
        // Draft rows don't exist on the backend yet; readers can't write;
        // the legacy backend silently ignores availability, so fail closed
        // (the view also hides the column for legacy pools).
        if (isDraftRowId(rowId)) return;
        if (!this.#deps.canWrite.value || this.#deps.isLegacyPool.value) return;
        const row = this.serverRows.value.find((r) => r.id === rowId);
        if (!row || row.available === available) return;
        // One in-flight PATCH per row: the toggle is disabled while its id
        // is in `pendingAvailabilityRowIds`, this backstops other callers.
        if (this.#itemAvailabilityOverrides.value.has(rowId)) return;

        const optimistic = new Map(this.#itemAvailabilityOverrides.value);
        optimistic.set(rowId, available);
        this.#itemAvailabilityOverrides.value = optimistic;
        const version = (this.#itemAvailabilityMutationVersion.get(rowId) ?? 0) + 1;
        this.#itemAvailabilityMutationVersion.set(rowId, version);
        const clearOverride = () => {
            const next = new Map(this.#itemAvailabilityOverrides.value);
            next.delete(rowId);
            this.#itemAvailabilityOverrides.value = next;
        };

        void updatePoolItem({ poolId, itemId: rowId, request: { available } }).then(
            async () => {
                if (this.#abort.signal.aborted || this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                // Reseat `available` from the backend before dropping the
                // override so the toggle can't flash back to the stale
                // value; availability changes also land on the change log.
                // The PATCH itself succeeded, so a failed invalidation must
                // not leave the row stuck in the pending set — clear the
                // override either way and let the next refetch reconcile.
                try {
                    await Promise.all([
                        invalidatePoolItemsQuery(this.#deps.queryClient, poolId),
                        invalidatePoolChangeLogQuery(this.#deps.queryClient, poolId),
                    ]);
                } catch {
                    // Fall through to the cleanup below.
                }
                if (this.#abort.signal.aborted || this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                clearOverride();
                this.#deps.onServerRowsRefetched();
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t(
                            available
                                ? 'admin::pool-management::toast-item-published'
                                : 'admin::pool-management::toast-item-suspended',
                            { symbol: row.symbol },
                        ),
                        group: 'default',
                        variant: 'success',
                    },
                });
            },
            async (error: unknown) => {
                if (this.#abort.signal.aborted || this.#itemAvailabilityMutationVersion.get(rowId) !== version) return;
                clearOverride();
                // Toast before the invalidation await: clearing the override
                // above releases the row for a new toggle, and a stale
                // failure toast must not land after that newer operation
                // already settled.
                const message = error instanceof Error ? error.message : String(error);
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-item-availability-failed', {
                            symbol: row.symbol,
                            message,
                        }),
                        group: 'default',
                        variant: 'error',
                    },
                });
                try {
                    await invalidatePoolItemsQuery(this.#deps.queryClient, poolId);
                } catch {
                    // Best-effort cache refresh on rollback — a failed
                    // invalidation just leaves the stale entry until the
                    // next fetch, and must not surface as an unhandled
                    // rejection.
                }
            },
        );
    }

    // --- CSV export ---
    readonly isDownloadingCsv: ReadonlySignal<boolean> = computed(() => this.#state.value.isDownloadingCsv);

    async downloadCsv(): Promise<void> {
        if (this.#abort.signal.aborted) return;
        if (typeof document === 'undefined') return;
        // Guard re-entrancy: the button also disables on this flag, but a
        // keyboard-repeat or programmatic caller could still fire twice.
        if (this.#state.value.isDownloadingCsv) return;
        // Flag in-flight so the toolbar can disable the button and spin a
        // loader; the fetch below can take a moment on a large pool.
        this.#dispatch(startCsvDownload());
        try {
            // The user expects the full pool, not just the current page.
            // Gather it via real backend pagination; the cache key is shared
            // with the model's "all rows" / substring-search fetches, so this
            // usually completes against an already-warm entry.
            const allOptions = getAllPoolItemsQuery(this.poolId);
            const response = await this.#deps.queryClient.fetchQuery({
                ...allOptions,
                queryKey: allOptions.queryKey ?? [],
            });
            if (this.#abort.signal.aborted) return;
            // Drop rows the user has staged for removal: keeping them in the
            // export would let them re-upload the file and silently undo the
            // pending delete. Drafts (pending adds + field edits) aren't in
            // the full-pool server response anyway, so this only needs to
            // filter the pending-removal set.
            const pendingRemovals = this.pendingRowRemovals.value;
            const rows = response.items.map(fromApiItem).filter((row) => !pendingRemovals.has(row.id));
            const csv = createInventoryCsv(rows, this.showFirmBorrowStatus.value);
            // Filename pattern: `Retail_Pool_2026-04-29.csv`. Date is the
            // local ISO yyyy-mm-dd so two exports on the same day overwrite
            // cleanly in the Downloads dir.
            const safeName = sanitizeFilenameSegment(this.#deps.poolName.value ?? '') || 'pool';
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, '0');
            const dd = String(today.getDate()).padStart(2, '0');
            triggerCsvDownload(csv, `${safeName}_${yyyy}-${mm}-${dd}.csv`);
        } catch (error) {
            // Surface a failed full-pool fetch like every other failure
            // path instead of leaking an unhandled rejection to callers
            // that fire-and-forget this command. Not after disposal: the
            // page that owned the toast is gone.
            if (this.#abort.signal.aborted) return;
            const message = error instanceof Error ? error.message : String(error);
            this.#deps.store.dispatch({
                type: 'toast::enqueue',
                toast: {
                    message: t('admin::pool-management::toast-csv-export-failed', { message }),
                    group: 'default',
                    variant: 'error',
                },
            });
        } finally {
            // Disposal during the fetch returns through here; the dead
            // instance must not take another reducer dispatch.
            if (!this.#abort.signal.aborted) this.#dispatch(endCsvDownload());
        }
    }

    // --- Upload flow (owned here: uploads modify this inventory) ---
    readonly #uploadModel = signal<PoolInventoryUploadModelImpl | null>(null);
    readonly uploadModel: ReadonlySignal<PoolInventoryUploadModel | null> = computed(() => this.#uploadModel.value);

    openUploadWizard(): void {
        if (!this.#deps.canWrite.value) return;
        this.#constructUploadWizard(this.#deps.canWrite);
    }
    openUploadWizardForCreatedPool(): void {
        // The creator just made the pool, so the pool-list refetch carrying
        // its permissions may not have landed: the wizard itself must not
        // consult the (still-false) write gate either, or it would open but
        // refuse to select or apply. The backend enforces access regardless.
        this.#constructUploadWizard(ALWAYS_WRITABLE);
    }
    #constructUploadWizard(canWrite: ReadonlySignal<boolean>): void {
        if (this.#uploadModel.value !== null) return;
        const poolId = this.poolId;
        this.#uploadModel.value = new PoolInventoryUploadModelImpl({
            poolId,
            queryObserverModelFactory: this.#deps.queryObserverModelFactory,
            queryClient: this.#deps.queryClient,
            store: this.#deps.store,
            canWrite,
            onServerRowsRefetched: () => this.#deps.onServerRowsRefetched(),
            onApplySucceeded: () => {
                // Upload supersedes any pending local adds for the pool.
                // Drop staged field edits keyed by the vanished draft ids
                // too, or they orphan in `rowEdits` (nothing else targets
                // those ids again) and keep the model invisibly dirty.
                const draftIds = this.#pendingAdds.value.map((row) => row.id);
                this.#pendingAdds.value = [];
                if (draftIds.length > 0) this.#dispatch(clearStagedChanges(draftIds, []));
            },
        });
    }
    closeUploadWizard(): void {
        const upload = this.#uploadModel.value;
        if (!upload) return;
        // Short-circuit when a real apply is in-flight so the user can't
        // accidentally abandon it; disposal (navigation) bypasses this
        // guard by disposing the whole inventory model.
        if (upload.isApplying.value) return;
        this.#teardownUploadWizard();
    }
    #teardownUploadWizard(): void {
        const upload = this.#uploadModel.value;
        if (!upload) return;
        this.#uploadModel.value = null;
        upload[Symbol.dispose]();
    }

    #startActionToast(message: string): string {
        const id = crypto.randomUUID();
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: { id, message, group: 'default', variant: 'info', autoHideDuration: null },
        });
        return id;
    }

    #replaceToast(startId: string, finalToast: { message: string; variant: 'success' | 'error' }): void {
        this.#deps.store.dispatch({ type: 'toast::dismiss', id: startId });
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: { message: finalToast.message, group: 'default', variant: finalToast.variant },
        });
    }

    /** Aborted on disposal so in-flight submit continuations bail out. */
    readonly #abort = new AbortController();

    [Symbol.dispose](): void {
        this.#abort.abort();
        this.#teardownUploadWizard();
        this.#disposableStack.dispose();
    }
}
